import React, { useEffect, useState } from 'react';
import { BadgeCheck } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import styles from './InvoiceDocument.module.css';
import {
  amountInWords,
  buildUpiUrl,
  fmtDate as formatDate,
  formatMoneyINR as formatMoney,
  getInvoiceBrand,
  getInvoiceLines,
  getPaymentInfo,
} from '../Invoiceexport';

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return value !== undefined && value !== null && value !== '' && Number.isFinite(n) ? n : fallback;
};

const DEFAULT_OPTIONS = {
  showLogo: true,
  showTaxBreakup: true,
  showAmountWords: true,
  showQr: true,
  showTerms: true,
};

const initialsOf = (name = '') =>
  String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('') || 'S';

/* Logo keeps its own shape (square or wide), never stretches,
   falls back to initials if the URL is broken. */
const BrandLogo = ({ src, name, imgClass, fallbackClass }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  if (!src || failed) {
    return fallbackClass ? <div className={fallbackClass}>{initialsOf(name)}</div> : null;
  }
  return <img src={src} alt={`${name} logo`} className={imgClass} onError={() => setFailed(true)} draggable={false} />;
};

const InvoiceDocument = ({ invoice, settings = {}, store = null, template, options }) => {
  const opt = { ...DEFAULT_OPTIONS, ...(options || {}) };

  const brand = getInvoiceBrand(settings, invoice, store);
  const invoicing = settings.invoicing || {};
  const isThermal = Boolean(template?.isThermal);

  const showTaxBreakup = opt.showTaxBreakup && invoicing.showTaxBreakup !== false;
  const lines = getInvoiceLines(invoice);
  const hasTax = showTaxBreakup && lines.some((l) => l.gstAmount > 0 || l.gstRate > 0);

  const gross = toNumber(invoice.summary?.sumGross, lines.reduce((s, l) => s + l.enteredRate * l.qty, 0));
  const itemDiscount = toNumber(invoice.summary?.sumItemDiscount);
  const taxable = toNumber(invoice.summary?.sumTaxable, lines.reduce((s, l) => s + l.taxableAmount, 0));
  const taxTotal = toNumber(invoice.summary?.sumTax, lines.reduce((s, l) => s + l.gstAmount, 0));
  const overallDiscount = toNumber(invoice.summary?.finalOverallDisc);
  const roundOff = toNumber(invoice.summary?.roundOffAmt);
  const total = toNumber(invoice.summary?.grandTotal);
  const itemCount = lines.reduce((s, l) => s + (Number(l.qty) || 0), 0);

  // Intra-state salon bill: GST shown as CGST + SGST halves
  const splitGst = brand.isGstBill && invoicing.gstSplit !== false;
  const halfTax = taxTotal / 2;

  const pay = getPaymentInfo(invoice);
  const paidVia = pay.modes.map((m) => m.label).join(', ');

  const invoiceNo = invoice.invoiceNo || invoice.invoiceMeta?.invoiceNo || '—';
  const docTitle = brand.isGstBill ? 'Tax Invoice' : 'Invoice';
  const customerName =
    invoice.customer?.name && !/draft customer/i.test(invoice.customer.name) ? invoice.customer.name.trim() : 'Walk-in customer';
  const customerPhone = invoice.customer?.phone || '';

  const qrAmount = pay.balanceDue > 0.01 ? pay.balanceDue : total;
  const upiUrl = brand.upiId
    ? buildUpiUrl({ upiId: brand.upiId, name: brand.salonName, amount: qrAmount, note: `Invoice ${invoiceNo}` })
    : '';
  const showQr = opt.showQr && Boolean(upiUrl) && (invoice.status === 'draft' || pay.balanceDue > 0.01);
  const contactLine = brand.contactLine || [brand.phone, brand.email].filter(Boolean).join('  |  ');
  const paperClass = styles[template?.paperClass] || '';

  const taxRows = (Row) =>
    hasTax && taxTotal > 0
      ? splitGst
        ? [<Row key="c" label="CGST" value={halfTax} />, <Row key="s" label="SGST" value={halfTax} />]
        : [<Row key="g" label="GST" value={taxTotal} />]
      : null;

  // =======================================================================
  // THERMAL RECEIPT (80mm / 58mm)
  // =======================================================================
  if (isThermal) {
    const is58 = template.paperClass === 'paperThermal58';
    const TRow = ({ label, value, minus }) => (
      <div className={styles.tSumRow}>
        <span>{label}</span>
        <span>{minus ? '- ' : ''}{formatMoney(value)}</span>
      </div>
    );

    return (
      <article className={`${styles.thermalWrapper} ${paperClass}`}>
        <header className={styles.tHeader}>
          {opt.showLogo && brand.logoUrl && <BrandLogo src={brand.logoUrl} name={brand.salonName} imgClass={styles.tLogo} />}
          <strong className={styles.tTitle}>{brand.salonName}</strong>
          {brand.address && <div className={styles.tAddress}>{brand.address}</div>}
          {brand.phone && <div>Ph: {brand.phone}</div>}
          {brand.gstin && <div className={styles.tGstin}>GSTIN: {brand.gstin}</div>}
        </header>

        <div className={styles.tInvoiceType}>{docTitle}</div>

        <div className={styles.tMetaGrid}>
          <span>Bill No</span><strong>{invoiceNo}</strong>
          <span>Date</span><strong>{formatDate(invoice.date)}</strong>
          <span>Customer</span><strong>{customerName}</strong>
          {customerPhone && (<><span>Phone</span><strong>{customerPhone}</strong></>)}
        </div>

        <div className={styles.tDivider} />

        <div className={styles.tTh}>
          <div className={styles.tCol1}>Item</div>
          {hasTax && !is58 && <div className={styles.tCol2}>GST</div>}
          <div className={styles.tCol3}>Amount</div>
        </div>

        <div className={styles.tItems}>
          {lines.map((item, index) => (
            <div key={index} className={styles.tTr}>
              <div className={styles.tCol1}>
                <div className={styles.tItemName}>{item.itemName}</div>
                <div className={styles.tSub}>
                  {item.qty} x {formatMoney(item.taxableRate || item.enteredRate)}
                  {item.discountAmount > 0 ? `  (-${formatMoney(item.discountAmount)})` : ''}
                  {hasTax && is58 && item.gstRate ? `  +${item.gstRate}%` : ''}
                </div>
              </div>
              {hasTax && !is58 && <div className={styles.tCol2}>{item.gstRate ? `${item.gstRate}%` : '-'}</div>}
              <div className={styles.tCol3}>{formatMoney(item.amount)}</div>
            </div>
          ))}
        </div>

        <div className={styles.tDivider} />

        <div className={styles.tSummary}>
          <div className={styles.tSumRow}><span>Items / Qty</span><span>{lines.length} / {itemCount}</span></div>
          <TRow label="Subtotal" value={gross} />
          {itemDiscount > 0 && <TRow label="Item discount" value={itemDiscount} minus />}
          {hasTax && taxTotal > 0 && <TRow label="Taxable value" value={taxable} />}
          {taxRows(TRow)}
          {overallDiscount > 0 && <TRow label="Overall discount" value={overallDiscount} minus />}
          {roundOff !== 0 && <TRow label="Round off" value={roundOff} />}
        </div>

        <div className={styles.tGrandTotal}>
          <span>TOTAL</span>
          <span>{formatMoney(total)}</span>
        </div>

        {opt.showAmountWords && <div className={styles.tWords}>{amountInWords(total)}</div>}

        {pay.balanceDue > 0.01 && (
          <div className={styles.tPayments}>
            <div className={`${styles.tSumRow} ${styles.tStrong}`}><span>Balance due</span><span>{formatMoney(pay.balanceDue)}</span></div>
          </div>
        )}

        {showQr && (
          <div className={styles.tQrSection}>
            <QRCodeSVG value={upiUrl} size={is58 ? 96 : 118} level="M" includeMargin={false} />
            <strong>Scan to pay via UPI</strong>
            <span>{brand.upiId}</span>
          </div>
        )}

        {opt.showTerms && brand.terms && <div className={styles.tTerms}>{brand.terms}</div>}

        <div className={styles.tFooterNote}>{brand.footerNote || 'Thank you! Visit again.'}</div>
      </article>
    );
  }

  // =======================================================================
  // A4 INVOICE
  // =======================================================================
  const themeClass = styles[template?.themeClass] || '';
  const CalcRow = ({ label, value, minus }) => (
    <div className={`${styles.calcRow} ${minus ? styles.calcRowDiscount : ''}`}>
      <span>{label}</span>
      <strong>{minus ? '- ' : ''}{formatMoney(value)}</strong>
    </div>
  );

  return (
    <article className={`${styles.invoicePaper} ${paperClass} ${themeClass}`}>
      <div className={styles.paperAccentTop} />

      <header className={styles.headerSection}>
        <div className={styles.brand}>
          {opt.showLogo && (
            <BrandLogo src={brand.logoUrl} name={brand.salonName} imgClass={styles.brandLogo} fallbackClass={styles.logoPlaceholder} />
          )}
          <div className={styles.brandText}>
            <h1 className={styles.companyName}>{brand.salonName}</h1>
            {brand.legalName && brand.legalName !== brand.salonName && <p className={styles.companyLegal}>{brand.legalName}</p>}
            {brand.tagline && <p className={styles.companyTagline}>{brand.tagline}</p>}
            {brand.address && <p className={styles.companyAddress}>{brand.address}</p>}
            {contactLine && <p className={styles.companyContact}>{contactLine}</p>}
          </div>
        </div>

        <div className={styles.invoiceMeta}>
          <span className={styles.invoiceTitle}>{docTitle}</span>
          <strong className={styles.invoiceNumber}>#{invoiceNo}</strong>
          {brand.gstin && (
            <span className={styles.gstinChip}>
              <BadgeCheck size={12} /> GSTIN {brand.gstin}
            </span>
          )}
        </div>
      </header>

      <section className={styles.infoGrid}>
        <div className={`${styles.infoCell} ${styles.infoCellWide}`}>
          <span className={styles.sectionLabel}>Billed to</span>
          <strong className={styles.customerName}>{customerName}</strong>
          {customerPhone && <span className={styles.customerMeta}>+91 {customerPhone}</span>}
        </div>
        <div className={styles.infoCell}>
          <span className={styles.sectionLabel}>Invoice date</span>
          <strong>{formatDate(invoice.date)}</strong>
        </div>
        <div className={styles.infoCell}>
          <span className={styles.sectionLabel}>Payment</span>
          <strong>{paidVia || (invoice.status === 'draft' ? 'Pending' : '—')}</strong>
        </div>
      </section>

      <section className={styles.tableSection}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th className={styles.colIndex}>#</th>
              <th className={styles.colItem}>Service / product</th>
              <th className={styles.colQty}>Qty</th>
              <th className={styles.colRate}>Rate</th>
              {hasTax && <th className={styles.colTax}>GST</th>}
              <th className={styles.colAmount}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((item, index) => (
              <tr key={index}>
                <td className={styles.colIndex}>{index + 1}</td>
                <td className={styles.colItem}>
                  <strong className={styles.itemName}>{item.itemName}</strong>
                  {item.discountAmount > 0 && (
                    <span className={styles.itemMetaDiscount}>Discount {formatMoney(item.discountAmount)}</span>
                  )}
                </td>
                <td className={styles.colQty}>{item.qty}</td>
                <td className={styles.colRate}>{formatMoney(item.taxableRate || item.enteredRate)}</td>
                {hasTax && <td className={styles.colTax}>{item.gstRate ? `${item.gstRate}%` : '-'}</td>}
                <td className={styles.colAmount}>{formatMoney(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className={styles.totalsSection}>
        <div className={styles.totalsLeft}>
          {opt.showAmountWords && (
            <div className={styles.wordsBox}>
              <span className={styles.sectionLabel}>Amount in words</span>
              <p>{amountInWords(total)}</p>
            </div>
          )}
          {brand.bankDetails && (
            <div className={styles.bankBox}>
              <span className={styles.sectionLabel}>Bank details</span>
              <p>{brand.bankDetails}</p>
            </div>
          )}
        </div>

        <div className={styles.totalsWrapper}>
          <CalcRow label="Subtotal" value={gross} />
          {itemDiscount > 0 && <CalcRow label="Item discount" value={itemDiscount} minus />}
          {hasTax && taxTotal > 0 && <CalcRow label="Taxable value" value={taxable} />}
          {taxRows(CalcRow)}
          {overallDiscount > 0 && <CalcRow label="Overall discount" value={overallDiscount} minus />}
          {roundOff !== 0 && <CalcRow label="Round off" value={roundOff} />}

          <div className={styles.grandTotalBox}>
            <span>Grand total</span>
            <strong>{formatMoney(total)}</strong>
          </div>

          {pay.balanceDue > 0.01 && (
            <div className={`${styles.calcRow} ${styles.calcRowDue}`}>
              <span>Balance due</span>
              <strong>{formatMoney(pay.balanceDue)}</strong>
            </div>
          )}
        </div>
      </section>

      <section className={styles.footerSection}>
        <div className={styles.footerDetails}>
          <p className={styles.footerNote}>{brand.footerNote || 'Thank you for visiting. See you again soon!'}</p>
          {opt.showTerms && brand.terms && <p className={styles.footerTerms}><b>Terms:</b> {brand.terms}</p>}
        </div>

        {showQr && (
          <div className={styles.qrBlock}>
            <QRCodeSVG value={upiUrl} size={80} level="M" includeMargin={false} />
            <div>
              <strong>Scan to pay</strong>
              <span>{brand.upiId}</span>
            </div>
          </div>
        )}
      </section>

      <div className={styles.bottomBar}>
        <span>{brand.isGstBill ? 'Computer generated tax invoice' : 'Computer generated invoice'}</span>
        <span>{brand.salonName}</span>
      </div>
    </article>
  );
};

export default InvoiceDocument;